/**
 * Canonical API client for authenticated and public frontend traffic.
 *
 * All callers must pass explicit backend paths (for example `/api/v1/...` or `/health`).
 */

import {
  clearPersistedAuthState,
  getBestAuthToken,
  getTenantScope,
  setActivePartitionScope,
  setOrganizationScope,
  withApiAuthHeaders,
} from '../utils/authHeaders';
import { getAuthBootstrapState, waitForAuthBootstrapReady } from './authBootstrap';
import {
  nextRequestGroupId,
  recordAuthCounter,
  recordAuthTiming,
} from './authDiagnostics';
import { emitAuthSessionInvalidated } from './authSessionEvents';
import { getLastRefreshAttemptInfo, refreshAccessToken } from '../utils/refreshAccessToken';
import {
  buildApiUrl,
} from './apiBase';

// Custom API Error class
export class ApiError extends Error {
  public status: number;
  public code?: string;
  public details?: unknown;

  constructor(message: string, options: { status: number; code?: string; details?: unknown }) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.details = options.details;
  }
}

export class TenantScopeMissingError extends ApiError {
  constructor(message = 'Organization scope missing. Please sign in again.') {
    super(message, { status: 401, code: 'tenant_scope_missing' });
    this.name = 'TenantScopeMissingError';
  }
}

class ApiClient {
  private retryAttempts = 3;
  private retryDelay = 1000; // 1 second
  private readonly readTimeoutMs = 12000; // Keep page loads responsive
  private readonly writeTimeoutMs = 15 * 60 * 1000; // Allow long generation workflows
  private readonly proactiveRefreshWindowMs = 60 * 1000;
  private tenantScopeHydrationInFlight: Promise<boolean> | null = null;

  constructor() {}

  private handleTerminalAuthFailure(path: string, status: number, reason: string): void {
    clearPersistedAuthState();
    emitAuthSessionInvalidated({
      reason,
      endpoint: path,
      status,
    });
  }

  private isPublicAuthEndpoint(url: string): boolean {
    return (
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password') ||
      url.includes('/auth/verify-email') ||
      url.includes('/simple-auth/')
    );
  }

  private isUnprotectedPath(url: string): boolean {
    return (
      this.isPublicAuthEndpoint(url) ||
      url.includes('/health') ||
      url.endsWith('/metrics') ||
      url.includes('/partner-intel/p/') ||  // Public partner dialogue token links
      url.includes('/hclp/') ||              // HCLP endpoints (feature-gated, no auth required)
      url.includes('/public/hclp/')          // HCLP public endpoints
    );
  }

  private isProtectedRequest(url: string, headers: Headers): boolean {
    if (this.isUnprotectedPath(url)) {
      return false;
    }
    if (!url.includes('/api/')) {
      return false;
    }
    return Boolean(headers.get('Authorization'));
  }

  private ensureRequestGroupHeader(headers: Headers): string {
    const existing = (headers.get('X-Request-Group-ID') || '').trim();
    const requestGroupId = existing || nextRequestGroupId();
    headers.set('X-Request-Group-ID', requestGroupId);
    return requestGroupId;
  }

  private setRequestAttemptHeaders(
    headers: Headers,
    requestGroupId: string,
    attemptLabel: string,
    refreshAttempted = false,
  ): void {
    headers.set('X-Request-ID', `${requestGroupId}-${attemptLabel}`);
    if (refreshAttempted) {
      headers.set('X-Auth-Refresh-Attempt', '1');
    } else {
      headers.delete('X-Auth-Refresh-Attempt');
    }
  }

  private shouldInvalidateSessionOnRefreshFailure(): boolean {
    const info = getLastRefreshAttemptInfo();
    return Boolean(info && !info.success && info.terminal);
  }

  private getBearerToken(headers: Headers): string | null {
    const authHeader = headers.get('Authorization');
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return null;
    }
    const token = authHeader.slice('Bearer '.length).trim();
    return token || null;
  }

  private decodeTokenExpMs(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }

      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64)) as { exp?: number };
      if (typeof payload.exp !== 'number') {
        return null;
      }
      return payload.exp * 1000;
    } catch {
      return null;
    }
  }

  private async maybeRefreshExpiringAccessToken(headers: Headers, url: string): Promise<void> {
    if (this.isPublicAuthEndpoint(url)) {
      return;
    }

    const token = this.getBearerToken(headers);
    if (!token) {
      return;
    }

    const expMs = this.decodeTokenExpMs(token);
    if (expMs === null) {
      return;
    }

    const msUntilExpiry = expMs - Date.now();
    if (msUntilExpiry > this.proactiveRefreshWindowMs) {
      return;
    }

    const refreshed = await refreshAccessToken();
    if (refreshed) {
      headers.set('Authorization', `Bearer ${refreshed}`);
    }
  }

  private async waitForProtectedRequestGate(headers: Headers, url: string): Promise<number> {
    headers.delete('X-Auth-Bootstrap-Wait-Ms');

    if (!this.isProtectedRequest(url, headers)) {
      return 0;
    }

    // The auth bootstrap itself needs to call /auth/me without waiting.
    if (url.includes('/api/v1/auth/me')) {
      return 0;
    }

    const bootstrapState = getAuthBootstrapState();
    if (bootstrapState.ready || bootstrapState.startedAt === null) {
      return 0;
    }

    const waitOutcome = await waitForAuthBootstrapReady(5000);
    const waitedMs = Math.max(0, waitOutcome.waitedMs);
    if (waitedMs > 0) {
      headers.set('X-Auth-Bootstrap-Wait-Ms', String(waitedMs));
      recordAuthCounter('auth.protected_call_pre_ready_blocked');
      recordAuthTiming('auth.bootstrap_delay', waitedMs, {
        timed_out: waitOutcome.timedOut,
      });
    }
    return waitedMs;
  }

  private async hydrateTenantScopeFromProfile(authHeader: string): Promise<boolean> {
    if (this.tenantScopeHydrationInFlight) {
      return this.tenantScopeHydrationInFlight;
    }

    this.tenantScopeHydrationInFlight = (async (): Promise<boolean> => {
      try {
        const requestGroupId = nextRequestGroupId();
        const profileResponse = await fetch(this.buildUrl('/api/v1/auth/me'), {
          method: 'GET',
          headers: {
            Authorization: authHeader,
            'X-Request-Group-ID': requestGroupId,
            'X-Request-ID': `${requestGroupId}-hydrate`,
          },
        });

        if (!profileResponse.ok) {
          return false;
        }

        const profile = (await profileResponse.json()) as {
          organization_id?: string | null;
          partition_id?: string | null;
          role?: string | null;
          assigned_partition_ids?: string[] | null;
        };

        const organizationId = String(profile?.organization_id || '').trim();
        if (!organizationId) {
          return false;
        }

        setOrganizationScope(organizationId);

        const role = String(profile?.role || '').trim();
        const assignedPartitions = Array.isArray(profile?.assigned_partition_ids)
          ? profile.assigned_partition_ids.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
          : [];
        const partitionId = String(profile?.partition_id || '').trim();

        if (role === 'agency_operator') {
          if (assignedPartitions.length === 1) {
            setActivePartitionScope(assignedPartitions[0]);
          }
        } else if (partitionId) {
          setActivePartitionScope(partitionId);
        }

        return true;
      } catch {
        return false;
      } finally {
        this.tenantScopeHydrationInFlight = null;
      }
    })();

    return this.tenantScopeHydrationInFlight;
  }

  private async ensureTenantScopeHeader(headers: Headers, url: string): Promise<void> {
    if (headers.has('X-Organization-ID')) {
      return;
    }

    const scope = getTenantScope();
    if (scope.organizationId) {
      headers.set('X-Organization-ID', scope.organizationId);
      return;
    }

    if (this.isUnprotectedPath(url)) {
      return;
    }

    const hasAuthHeader = Boolean(headers.get('Authorization'));
    if (!hasAuthHeader || this.isPublicAuthEndpoint(url)) {
      return;
    }

    const authHeader = headers.get('Authorization');
    if (!authHeader) {
      if (this.isUnprotectedPath(url)) return;
      throw new TenantScopeMissingError();
    }

    const hydrated = await this.hydrateTenantScopeFromProfile(authHeader);
    if (!hydrated) {
      if (this.isUnprotectedPath(url)) return;
      throw new TenantScopeMissingError();
    }

    const refreshedScope = getTenantScope();
    if (refreshedScope.organizationId) {
      headers.set('X-Organization-ID', refreshedScope.organizationId);
      return;
    }

    if (!this.isUnprotectedPath(url)) {
      throw new TenantScopeMissingError();
    }
  }

  private buildUrl(path: string): string {
    return buildApiUrl(path);
  }

  private async parseJson(response: Response): Promise<unknown> {
    if (response.status === 204) {
      return null;
    }

    const rawBody = await response.text();
    if (!rawBody) {
      return null;
    }

    try {
      return JSON.parse(rawBody);
    } catch {
      return rawBody;
    }
  }

  private extractApiErrorMessage(errorData: unknown): string | null {
    if (typeof errorData === 'string') {
      const trimmed = errorData.trim();
      return trimmed.length > 0 ? trimmed : null;
    }

    if (typeof errorData === 'object' && errorData !== null) {
      const payload = errorData as Record<string, unknown>;

      // Priority 1: Direct 'message' or 'detail' or 'error' if they are strings
      for (const key of ['message', 'detail', 'error']) {
        const val = payload[key];
        if (typeof val === 'string' && val.trim()) {
          return val;
        }
      }

      // Priority 2: Nested objects (recursive search for 'message' or 'error')
      for (const key of ['error', 'detail', 'message']) {
        const val = payload[key];
        if (typeof val === 'object' && val !== null) {
          const nested = this.extractApiErrorMessage(val);
          if (nested) return nested;
        }
      }
    }

    return null;
  }

  async apiFetch(path: string, init: RequestInit = {}): Promise<any> {
    const url = this.buildUrl(path);

    // Add default headers and auth token.
    const headers = withApiAuthHeaders(init.headers);
    if (!(init.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    } else {
      headers.delete('Content-Type');
    }

    if (!headers.has('Authorization')) {
      const token = getBestAuthToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const requestGroupId = this.ensureRequestGroupHeader(headers);
    await this.maybeRefreshExpiringAccessToken(headers, url);
    await this.waitForProtectedRequestGate(headers, url);
    await this.ensureTenantScopeHeader(headers, url);

    const requestInit: RequestInit = {
      ...init,
      headers,
    };

    const method = (init.method || 'GET').toUpperCase();
    const isReadRequest = this.isIdempotent(method);
    const timeoutMs = isReadRequest ? this.readTimeoutMs : this.writeTimeoutMs;
    const maxAttempts = isReadRequest ? 2 : 1;

    let lastError: ApiError | null = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        this.setRequestAttemptHeaders(headers, requestGroupId, `a${attempt}`);
        const timeoutController = new AbortController();
        const existingSignal = requestInit.signal;
        const timeoutId = globalThis.setTimeout(() => {
          timeoutController.abort();
        }, timeoutMs);
        const onExistingAbort = () => timeoutController.abort();

        if (existingSignal) {
          if (existingSignal.aborted) {
            timeoutController.abort();
          } else {
            existingSignal.addEventListener('abort', onExistingAbort, { once: true });
          }
        }

        let response: Response;
        try {
          response = await fetch(url, {
            ...requestInit,
            signal: timeoutController.signal,
          });
        } finally {
          globalThis.clearTimeout(timeoutId);
          if (existingSignal) {
            existingSignal.removeEventListener('abort', onExistingAbort);
          }
        }

        if (response.status === 401) {
          // If this is an unprotected or public endpoint (like /public/hclp/:token, /health, etc.),
          // a 401 must NEVER invalidate or terminate the user's logged-in session!
          if (this.isUnprotectedPath(url)) {
            const errorData = await this.parseJson(response);
            const message = this.extractApiErrorMessage(errorData) || 'Unauthorized request to public resource';
            throw new ApiError(message, { status: 401, details: errorData });
          }

          const isAuthEndpoint = url.includes('/auth/') || url.includes('/simple-auth/');
          if (isAuthEndpoint) {
            this.handleTerminalAuthFailure(path, 401, 'auth_endpoint_401');
            if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
              window.location.href = '/login';
            }
            throw new ApiError('Authentication required', { status: 401 });
          }

          const hadAuthHeader = Boolean(headers.get('Authorization'));
          if (hadAuthHeader) {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              headers.set('Authorization', `Bearer ${refreshed}`);
              this.setRequestAttemptHeaders(headers, requestGroupId, `refresh${attempt}`, true);
              const retryResponse = await fetch(url, { ...requestInit, headers });
              if (retryResponse.ok) {
                return await this.parseJson(retryResponse);
              }
              if (retryResponse.status !== 401) {
                const retryErrorData = await this.parseJson(retryResponse);
                const retryStatusText = retryResponse.statusText || `HTTP ${retryResponse.status}`;
                const retryMessage =
                  this.extractApiErrorMessage(retryErrorData) || `API Error: ${retryStatusText}`;
                throw new ApiError(retryMessage, {
                  status: retryResponse.status,
                  details: retryErrorData,
                });
              }
              recordAuthCounter('auth.401.after_refresh', 1, { endpoint: path });
              this.handleTerminalAuthFailure(path, 401, 'refresh_retry_401');
            }
            if (!refreshed && this.shouldInvalidateSessionOnRefreshFailure()) {
              this.handleTerminalAuthFailure(path, 401, 'refresh_unavailable');
            }
          }

          throw new ApiError('Authentication required', { status: 401 });
        }

        if (response.status >= 500 && this.isIdempotent(init.method)) {
          if (attempt < this.retryAttempts) {
            await this.delay(this.retryDelay * attempt);
            continue;
          }
        }

        if (!response.ok) {
          const errorData = await this.parseJson(response);
          const statusText = response.statusText || `HTTP ${response.status}`;
          const message = this.extractApiErrorMessage(errorData) || `API Error: ${statusText}`;
          throw new ApiError(message, {
            status: response.status,
            details: errorData,
          });
        }

        return await this.parseJson(response);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          lastError = new ApiError(
            `Request timed out after ${Math.round(timeoutMs / 1000)}s`,
            { status: 408 }
          );
        } else {
          lastError =
            error instanceof ApiError
              ? error
              : new ApiError(
                  `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  { status: 0 }
                );
        }

        if (attempt === maxAttempts || !this.isRetriableError(lastError)) {
          break;
        }

        await this.delay(this.retryDelay * attempt);
      }
    }

    if (lastError) {
      throw lastError;
    }

    throw new ApiError('Unknown error occurred', { status: 0 });
  }

  /**
   * Raw response variant for call-sites that still need explicit status/error-body handling.
   * Uses the same auth header and refresh policy as apiFetch().
   */
  async apiRequest(path: string, init: RequestInit = {}): Promise<Response> {
    const url = this.buildUrl(path);
    const headers = withApiAuthHeaders(init.headers);

    if (!(init.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (init.body instanceof FormData) {
      headers.delete('Content-Type');
    }

    if (!headers.has('Authorization')) {
      const token = getBestAuthToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const requestGroupId = this.ensureRequestGroupHeader(headers);
    await this.maybeRefreshExpiringAccessToken(headers, url);
    await this.waitForProtectedRequestGate(headers, url);
    await this.ensureTenantScopeHeader(headers, url);

    const requestInit: RequestInit = {
      ...init,
      headers,
    };

    this.setRequestAttemptHeaders(headers, requestGroupId, 'a1');
    let response = await fetch(url, requestInit);
    if (response.status !== 401) {
      return response;
    }

    // Public / unprotected endpoints (e.g. /public/hclp/...) must never terminate user session on 401
    if (this.isUnprotectedPath(url)) {
      return response;
    }

    const isAuthEndpoint = url.includes('/auth/') || url.includes('/simple-auth/');
    if (isAuthEndpoint) {
      this.handleTerminalAuthFailure(path, 401, 'auth_endpoint_401');
      return response;
    }

    const hadAuthHeader = Boolean(headers.get('Authorization'));
    if (!hadAuthHeader) {
      return response;
    }

    const refreshed = await refreshAccessToken();
    if (!refreshed && this.shouldInvalidateSessionOnRefreshFailure()) {
      this.handleTerminalAuthFailure(path, 401, 'refresh_unavailable');
    }
    if (!refreshed) {
      return response;
    }

    headers.set('Authorization', `Bearer ${refreshed}`);
    this.setRequestAttemptHeaders(headers, requestGroupId, 'refresh1', true);
    response = await fetch(url, {
      ...requestInit,
      headers,
    });

    if (response.status === 401) {
      recordAuthCounter('auth.401.after_refresh', 1, { endpoint: path });
      this.handleTerminalAuthFailure(path, 401, 'refresh_retry_401');
    }

    return response;
  }

  private isIdempotent(method?: string): boolean {
    return ['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE'].includes(method?.toUpperCase() || 'GET');
  }

  private isRetriableError(error: ApiError): boolean {
    return error.message.includes('Network error') || error.status === 408 || error.status >= 500;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async checkHealth(): Promise<{ status: string; timestamp: Date }> {
    return this.apiFetch('/api/v1/health');
  }
}

export const apiClient = new ApiClient();
export const apiFetch = apiClient.apiFetch.bind(apiClient);
export const apiRequest = apiClient.apiRequest.bind(apiClient);
